import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { TreeSelect } from 'primeng/treeselect';
import { ToastService } from '../../../../services/toast.service';
import { Modulo } from '../../../../types/Modulo.type';
import { Widget } from '../../../../types/Widget.type';
import { PerfilService } from '../../perfil/perfil.service';
import { WidgetAdminService } from '../widget-admin.service';

@Component({
  selector: 'app-widgets-form',
  templateUrl: './widgets-form.component.html',
  styleUrl: './widgets-form.component.css'
})
export class WidgetsFormComponent implements OnInit {

  @ViewChild('seletorModulo') seletorModulo?: TreeSelect;

  widget: Widget = this.novoWidget();
  original?: Widget;

  modulos: TreeNode<Modulo>[] = [];
  moduloSelecionado: TreeNode<Modulo> | null = null;
  carregandoModulos = false;

  enviado = false;
  salvando = false;
  acessoNegado = false;

  validandoIdentificador = false;
  erroIdentificador: string | null = null;

  larguras = [1, 2, 3, 4];
  alturas = [1, 2];
  readonly celulasPreview = Array.from({ length: 8 }, (_, i) => ({ linha: Math.floor(i / 4), coluna: i % 4 }));

  constructor(
    private widgetAdminService: WidgetAdminService,
    private perfilService: PerfilService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmationService,
    private toastService: ToastService
  ) { }

  get edicao(): boolean {
    return !!this.widget.id;
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');

    this.carregandoModulos = true;
    this.perfilService.getPermissoes().subscribe({
      next: (data) => {
        this.modulos = this.paraNos(data);
        this.carregandoModulos = false;
        this.selecionarModuloDoWidget();
      },
      error: () => {
        this.carregandoModulos = false;
        this.toastService.erro('Erro', 'Erro ao buscar os módulos');
      }
    });

    if (id) {
      this.widgetAdminService.buscar(id).subscribe({
        next: (data) => {
          this.widget = data;
          this.original = { ...data };
          this.selecionarModuloDoWidget();
        },
        error: (error: HttpErrorResponse) => this.tratarErro(error, 'Erro ao buscar o widget')
      });
    }
  }

  ocupada(celula: { linha: number, coluna: number }): boolean {
    return celula.coluna < (this.widget.largura || 0) && celula.linha < (this.widget.altura || 0);
  }

  aoSelecionarModulo(no: TreeNode<Modulo>) {
    const modulo = no.data;
    this.widget.moduloId = modulo?.id as string;
    this.seletorModulo?.hide();   // o TreeSelect do PrimeNG 17 não fecha sozinho no modo single

    if (modulo && !this.edicao && !this.widget.identificador?.trim()) {
      this.widget.identificador = this.prefixo(modulo.nome);
      this.erroIdentificador = null;
    }
  }

  validarIdentificador() {
    const identificador = this.widget.identificador?.trim();
    this.erroIdentificador = null;

    if (this.edicao || !identificador || identificador.endsWith('.')) return;

    this.validandoIdentificador = true;
    this.widgetAdminService.identificadorDisponivel(identificador).subscribe({
      next: (disponivel) => {
        this.validandoIdentificador = false;
        if (!disponivel) this.erroIdentificador = 'Identificador já existe';
      },
      error: (error: HttpErrorResponse) => {
        this.validandoIdentificador = false;
        this.tratarErro(error, 'Erro ao validar o identificador');
      }
    });
  }

  salvar() {
    this.enviado = true;

    if (!this.valido()) return;

    if (!this.edicao) {
      this.salvando = true;
      this.widgetAdminService.identificadorDisponivel(this.widget.identificador.trim()).subscribe({
        next: (disponivel) => {
          this.salvando = false;
          disponivel ? this.enviar() : this.erroIdentificador = 'Identificador já existe';
        },
        error: (error: HttpErrorResponse) => {
          this.salvando = false;
          this.tratarErro(error, 'Erro ao validar o identificador');
        }
      });
      return;
    }

    if (this.tamanhoAlterado()) {
      this.widgetAdminService.emUso(this.widget.id!).subscribe({
        next: (quantidade) => quantidade > 0 ? this.confirmarAlteracaoTamanho(quantidade) : this.enviar(),
        error: (error: HttpErrorResponse) => this.tratarErro(error, 'Erro ao verificar o uso do widget')
      });
      return;
    }

    this.enviar();
  }

  voltar() {
    this.router.navigate(['administracao', 'widgets']);
  }

  private valido(): boolean {
    const w = this.widget;
    return !!(w.identificador?.trim() && !w.identificador.trim().endsWith('.') && w.nome?.trim() && w.descricao?.trim()
      && w.moduloId && w.largura && w.altura && !this.erroIdentificador);
  }

  private tamanhoAlterado(): boolean {
    return !!this.original && (this.original.largura !== this.widget.largura || this.original.altura !== this.widget.altura);
  }

  private confirmarAlteracaoTamanho(quantidade: number) {
    this.confirmationService.confirm({
      header: 'Alterar tamanho',
      message: `Este widget está no dashboard de ${quantidade} usuário(s). Ao alterar o tamanho, ele será retirado da posição atual e colocado no final do dashboard desses usuários. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: 'Continuar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.enviar()
    });
  }

  private enviar() {
    const widget: Widget = {
      ...this.widget,
      identificador: this.widget.identificador.trim(),
      nome: this.widget.nome.trim(),
      descricao: this.widget.descricao.trim(),
      icone: this.widget.icone?.trim() || null,
    };
    const avisoVisibilidade = this.avisoVisibilidade();

    this.salvando = true;
    const requisicao = this.edicao ? this.widgetAdminService.alterar(widget) : this.widgetAdminService.criar(widget);

    requisicao.subscribe({
      next: () => {
        this.salvando = false;
        this.toastService.sucesso(this.edicao ? 'Atualização' : 'Cadastro',
          this.edicao ? 'Atualização efetuada com sucesso.' : 'Cadastro efetuado com sucesso.');
        if (avisoVisibilidade) this.toastService.alerta('Aviso', avisoVisibilidade);
        this.voltar();
      },
      error: (error: HttpErrorResponse) => {
        this.salvando = false;
        this.tratarErro(error, 'Erro ao salvar o widget');
      }
    });
  }

  private avisoVisibilidade(): string | null {
    if (!this.original) return null;

    if (this.original.ativo && !this.widget.ativo) {
      return 'O widget foi inativado e deixará de aparecer no dashboard dos usuários. Ao reativar, ele volta ao final do dashboard.';
    }
    if (this.original.moduloId !== this.widget.moduloId) {
      return 'Usuários sem permissão no novo módulo deixarão de ver o widget. Ele volta ao final do dashboard quando voltar a ficar visível.';
    }
    return null;
  }

  private tratarErro(error: HttpErrorResponse, mensagem: string) {
    const texto = typeof error.error === 'string' && error.error ? error.error : mensagem;

    switch (error.status) {
      case 403:
        this.acessoNegado = true;
        break;
      case 404:
        this.toastService.erro('Erro', texto);
        this.voltar();
        break;
      case 409:
        this.erroIdentificador = texto;
        break;
      default:
        this.toastService.erro('Erro', texto);
        console.error(error);
    }
  }

  /** ROLE_INTEG_MOVIMEN_NOTAFIS → integ-movimen-notafis. */
  private prefixo(nomeModulo: string): string {
    return nomeModulo.replace(/^ROLE_/, '').toLowerCase().replace(/_/g, '-') + '.';
  }

  private paraNos(modulos: Modulo[]): TreeNode<Modulo>[] {
    return (modulos || []).map(m => ({
      key: m.id,
      label: m.descricao || m.nome,
      data: m,
      children: this.paraNos(m.modulosFilhos),
      leaf: !m.modulosFilhos?.length
    }));
  }

  private selecionarModuloDoWidget() {
    if (!this.widget.moduloId || !this.modulos.length) return;
    this.moduloSelecionado = this.buscarNo(this.modulos, this.widget.moduloId);
  }

  private buscarNo(nos: TreeNode<Modulo>[], id: string): TreeNode<Modulo> | null {
    for (const no of nos) {
      if (no.key === id) return no;
      const filho = this.buscarNo(no.children || [], id);
      if (filho) {
        no.expanded = true;
        return filho;
      }
    }
    return null;
  }

  private novoWidget(): Widget {
    return { identificador: '', nome: '', descricao: '', icone: null, moduloId: '', largura: 1, altura: 1, ativo: true };
  }
}
